/**
 * Pure slot logic for the inline share-form dialect.
 *
 * Two roles read this file. The BUILDER presents a template's manager-editable
 * middle as a stack of SLOTS — one per form element — and every mutation the
 * UI offers (retitle a select, add/remove an option, toggle the "Other"
 * write-in, resize a free-text limit, reorder, duplicate, delete) is a pure
 * function here; the component owns only React state and gesture handling. The
 * COMPOSER asks the same slots what they put into the message the sender is
 * writing, and whether the sender has answered them — see `ComposeLogic`,
 * the visitor that composes a form over these.
 *
 * Every slot carries a `type` tag, lifted from the YAML key it is authored
 * under, and that tag is what the author sees:
 *
 *   text      static text, nobody fills it
 *   goal      static text about the topic's impression goal
 *   optional  text the sender opts into as they compose
 *   select    end user picks an option (optionally writing one in)
 *   fillIn    end user types free text
 *
 * That tag is a DISPATCH KEY, not something the functions below read. Each type
 * declares its own behavior once — its author-facing name, whether it owns a
 * field id, what a blank one looks like, how it previews, when it warns, what
 * it contributes to a composed message and when it is answered — as one
 * `SlotTypeLogic` object, and the `SLOT_LOGIC` registry maps the tag to it.
 * `logic(slot)` is the lookup, so a function like `warning` is a one-liner
 * that hands the slot back to its own type's implementation. Those one-liners
 * are the whole surface: the lookup is private to this module, so nothing
 * outside reaches an entry to pass it the slot it was found with.
 *
 * The lookup hands back the registry entry itself rather than one bound to the
 * slot. A bound entry would have to be built per call, and `SlotViews` — the
 * rendering twin of this registry — cannot afford that: its members are
 * components, and a fresh identity each render would remount the open card on
 * every keystroke. Both registries answer the same way for the same reason.
 *
 * Adding a type therefore means adding one registry entry: the registry's
 * `satisfies` clause covers every tag in the union, so the compiler names the
 * type that is missing rather than leaving four switches to find.
 *
 * The "Other" write-in is deliberately NOT a type: per the form-building
 * pattern it is a property of a select slot, carried as that select's
 * `writeIn`.
 */

import {
  OTHER_WRITE_IN_MAX_LENGTH_DEFAULT,
  FREE_TEXT_MAX_LENGTH_DEFAULT,
  GOAL_SLOT_DEFAULT_TEXT,
} from '@/modules/config/constants';
import {
  isProcedureName,
  isVariableName,
  unknownPrimitives,
  tokenIssues,
  variableIssues,
  type FillInSlot,
  type GoalSlot,
  type OptionalSlot,
  type SelectSlot,
  type SlotWriteIn,
  type TextSlot,
} from './schema';
import { UUID80 } from '@/uuid_extensions/uuid80';
import type { AddressingMode, AddressingModeGate } from '@/modules/share';
import type { SharingModeGate } from '@/modules/template/types';
import type { TopicTypeId } from '@/modules/topic/types';
import type { AnsweredSlot, SharingFormSlot } from './types';
import {
  type Environment,
  ExpressionLogic,
  type Expression,
  PRIMITIVES,
  type PrimitiveName,
  type Statement,
} from './lang';

export type SlotType = SharingFormSlot['type'];

/** The slot of one particular type — `SlotOfType<'select'>` is a select slot. */
export type SlotOfType<T extends SlotType> = Extract<
  SharingFormSlot,
  { type: T }
>;

/** The free-text alternative a select offers alongside its presets. */
export type NestedWriteIn = SlotWriteIn;

// ── What a slot type knows about itself ─────────────────────────────────────

/**
 * What the builder knows about the topic on screen, for the types that are only
 * in play under some conditions, and for the editors that offer a choice drawn
 * from the rest of the setup. A type that does not care ignores it.
 */
export interface FormBuilderContext {
  /** Whether the topic, as drafted, sets an impression goal. */
  readonly hasImpressionGoal: boolean;
  /**
   * Every variable the topic's sharing setup assigns, across all its forms —
   * `FormLogic.variableNames` — which is what the variable picker offers.
   */
  readonly variableNames: readonly string[];
  /**
   * The field id of the slot the Settings tab takes the allowed detail from,
   * or null when this form has none (the detail is off, or comes from another
   * intent's form).
   */
  readonly allowedDetailSlotId?: string | null;
  /**
   * The topic's type, whose definitions (its `definitions.yaml`) the form
   * may read; with none, any type's.
   */
  readonly topicType?: TopicTypeId;
}

/**
 * One slot type's behavior, over slots already narrowed to that type.
 *
 * Members are declared as METHODS on purpose: TypeScript checks method
 * parameters bivariantly, which is what lets the registry hand a
 * `SlotTypeLogic<SelectSlot>` back through `logic()` as a
 * `SlotTypeLogic<SharingFormSlot>` — dynamic dispatch, the way an overridden
 * method would be. Each implementation still only ever sees its own slot,
 * because the callers below pass back the very slot they looked the logic up
 * with.
 */
export interface SlotTypeLogic<S extends SharingFormSlot = SharingFormSlot> {
  readonly type: S['type'];
  /**
   * The type's author-facing name — the one source for the collapsed card's
   * aria label, the edit card's eyebrow and the Add-slot sheet's entry, so
   * those three can never drift apart.
   */
  readonly name: string;
  /** A fresh slot of this type, as the ＋ Add slot sheet leaves it. */
  blank(): S;
  /** The one-line preview a collapsed card shows. */
  summary(slot: S): string;
  /**
   * Why this slot is not yet valid in a form for a topic of `topicType`, or
   * null when it is fine.
   */
  warning(slot: S, topicType?: TopicTypeId): string | null;
  /**
   * Whether the ＋ Add slot sheet offers this type against the topic on screen.
   * Most types are always on offer, so the question is the type's own to answer
   * rather than the sheet's to ask about particular tags.
   */
  includeTypeInSlotInsertionOptions(context: FormBuilderContext): boolean;
  /**
   * Whether a slot of this type still does its job against the topic on screen.
   * An unusable slot stays in the stack — still editable, still deletable — and
   * how that reads is the UI's call; this only answers whether what the slot
   * depends on is still there. A separate question from whether a NEW one is
   * worth offering.
   */
  isCurrentlyUsable(context: FormBuilderContext): boolean;
  /**
   * Whether a slot of this type takes the variable keys, `define` and
   * `dontEmit`. Select, Fill in and Text do; the rest neither assign a
   * variable nor can be left out of the message.
   */
  readonly takesVariable: boolean;

  // ── Composing a message ───────────────────────────────────────────────────

  /**
   * Whether the sender answers a slot of this type while composing. `text`
   * and `goal` are not the sender's to fill, though what they SAY can still depend
   * on an answer (see `EnvironmentLogic.resolve`); `select`, `fillIn` and
   * `optional` are answered directly.
   */
  readonly isAnswerable: boolean;
  /**
   * Whether the sender may answer in their own words rather than pick
   * something the slot declares — a `fillIn` always does, a `select` only when
   * it offers a write-in. It is what keeps a slot worth asking about even when
   * everything it declares resolves to nothing.
   */
  acceptsFreeText(slot: S): boolean;
  /**
   * The answers this slot can take, in the form an answer is STORED in: a
   * preset written as a braced primitive (`'{_profileName}'`) appears
   * here as the bare name. An answer is matched against these to tell a
   * declared choice from the sender's own words.
   *
   * Without an addressing mode, every answer the slot declares, whatever mode
   * the message is in. With one, the subset a control puts on screen in that
   * mode: an `optional` restricted to one addressing mode offers nothing in
   * the other; every other type offers everything it declares.
   */
  possibleValues(slot: S, addressingMode?: AddressingMode): readonly string[];
  /**
   * The statements this slot stands for, in the order they run (see
   * `Statement`), given the answer it carries:
   *
   *   select, fillIn  define its variable as its answer expression (see
   *                   `answerExpr`) unless the answer is blank, then emit
   *                   the variable, or the answer expression when it defines
   *                   none, unless `dontEmit`
   *   optional        emit its answer expression
   *   text            define its variable as its text, then emit the
   *                   variable unless `dontEmit`; with no variable, emit its
   *                   text unless `dontEmit`
   *   goal            emit its text
   */
  asStatements(slot: AnsweredSlot<S>): readonly Statement[];
  /**
   * What the answer this slot carries prints as, or null for a type the
   * sender does not answer: a picked option as a template, the sender's own
   * words as a quote, an optional's text or the quote `''` when left out.
   */
  answerExpr(slot: AnsweredSlot<S>): Expression | null;
  /**
   * Whether the sender has answered this slot, as far as this type is
   * concerned. A text or goal slot waits on nobody, and an untouched `optional` is a choice
   * rather than an omission, so only the field types can be unanswered.
   * Callers go through `SlotLogic.isAnswered`, which also excuses a slot
   * with nothing worth asking (see `isVestigial`).
   */
  isAnswered(slot: AnsweredSlot<S>, env: Environment): boolean;
}

/**
 * A static slot claims no name, never warns, is always on offer, and is not
 * the sender's to answer — so it declares nothing, offers nothing, and is
 * never waiting on them.
 */
const defaultSlotLogic = {
  warning: () => null,
  includeTypeInSlotInsertionOptions: () => true,
  isCurrentlyUsable: () => true,
  takesVariable: false,
  isAnswerable: false,
  acceptsFreeText: () => false,
  possibleValues: () => [],
  isAnswered: () => true,
  answerExpr: () => null,
} as const;

// ── The expr types: text, goal, optional ────────────────────────────────────

/** The slot types whose content is an `expr` the author writes. */
type ExprSlot = TextSlot | GoalSlot | OptionalSlot;

/**
 * The first primitive in `text` the app cannot resolve, so the builder
 * warns about a misspelling as the author types it, before the schema would
 * reject the form.
 */
function unknownPrimitive(text: string, type?: TopicTypeId): string | null {
  return unknownPrimitives(text, type)[0] ?? null;
}

/**
 * The expr types' shared warning: a misspelled primitive, then a token that
 * cannot be evaluated (a malformed or misapplied call), then nothing
 * to say at all. What counts as nothing differs by type, so each passes its own
 * `isBlank`.
 */
function exprWarning(isBlank: (text: string) => boolean) {
  return (slot: ExprSlot, type?: TopicTypeId): string | null => {
    const unknown = unknownPrimitive(slot.expr, type);
    if (unknown) return `Unknown primitive "${unknown}"`;
    const issue = tokenIssues(slot.expr, type)[0];
    if (issue) return issue;
    return isBlank(slot.expr) ? 'Needs some text' : null;
  };
}

/**
 * An `expr` previews as itself. Leading/trailing spaces are meaningful in this
 * dialect (they are the words' separators once the body is composed), so the
 * summary keeps them and the card renders them visibly.
 *
 * An `expr` is a template, with its tokens resolved where it is evaluated: a
 * primitive becomes its value for this message, a field id what that
 * slot evaluates to, and a variable what the slot defining it evaluates to.
 * Its spacing is kept as written, and the body's final tidy settles the
 * spacing around a token that came out blank.
 */
const exprSlotLogic = {
  ...defaultSlotLogic,
  summary: (slot: ExprSlot) => slot.expr,
  asStatements: (slot: AnsweredSlot<ExprSlot>): readonly Statement[] => [
    { kind: 'emit', expr: exprTemplate(slot) },
  ],
} as const;

/** A slot's `expr` as the template expression it evaluates. */
function exprTemplate(slot: ExprSlot): Expression {
  return ExpressionLogic.template(slot.expr, 'asWritten');
}

/**
 * A fresh id for a new slot: a random base64 UUID80, which no other slot in
 * the config will be using.
 */
function newSlotId(): string {
  return UUID80.new().toBase64();
}

// ── The field types: select, fillIn ─────────────────────────────────────────

/**
 * A field slot stores its answer under its `id`, and previews as the question
 * it asks. The id is an internal key (a UUID80 the builder generates) that is
 * never shown to the author, so the slot summarizes as its prompt.
 */
const fieldSlotLogic = {
  ...defaultSlotLogic,
  summary: (slot: SelectSlot | FillInSlot) => slot.prompt,
  isAnswerable: true,
  isAnswered: isFieldAnswered,
} as const;

/**
 * The warning every field slot shares: the sender is asked its prompt, so a
 * field slot cannot be completed without one. Its card stays open meanwhile,
 * so the warning never reaches a collapsed card; it keeps the form out of the
 * draft until the prompt is filled in.
 */
const MISSING_PROMPT = 'Needs a prompt';

function promptWarning(slot: SelectSlot | FillInSlot): string | null {
  return slot.prompt.trim() === '' ? MISSING_PROMPT : null;
}

/**
 * A field slot is answered once the sender has put something in it that
 * actually reaches the message. A picked option that prints nothing —
 * `{_profileName}` for a sender with no profile name — reads as filled while
 * sending nothing, so it does not count as an answer.
 */
function isFieldAnswered(
  slot: AnsweredSlot<SelectSlot | FillInSlot>,
  env: Environment
): boolean {
  if (slot.answer.trim() === '') return false;
  const picked = logic(slot)
    .possibleValues(slot)
    .find(value => value === slot.answer);
  if (picked === undefined) return true;
  return (
    ExpressionLogic.print(
      ExpressionLogic.template(picked, 'tidied'),
      env
    ).trim() !== ''
  );
}

// ── The registry ────────────────────────────────────────────────────────────

const TEXT: SlotTypeLogic<TextSlot> = {
  ...exprSlotLogic,
  type: 'text',
  takesVariable: true,
  name: 'Text',
  asStatements: slot => {
    const emits = slot.dontEmit !== true;
    if (!slot.define)
      return emits ? [{ kind: 'emit', expr: exprTemplate(slot) }] : [];
    const variable = slot.define;
    return [
      { kind: 'define', name: variable, expr: exprTemplate(slot) },
      ...(emits
        ? [{ kind: 'emit', expr: ExpressionLogic.variable(variable) } as const]
        : []),
    ];
  },
  blank: () => ({ type: 'text', id: newSlotId(), expr: '' }),
  // Empty, not blank: a text slot holding a single space is doing its job —
  // that is how words end up separated once the body composes.
  warning: exprWarning(text => text === ''),
};

const GOAL: SlotTypeLogic<GoalSlot> = {
  ...exprSlotLogic,
  type: 'goal',
  name: 'Goal',
  blank: () => ({
    type: 'goal',
    id: newSlotId(),
    expr: GOAL_SLOT_DEFAULT_TEXT,
  }),
  warning: exprWarning(text => text.trim() === ''),
  // The slot reports on the impression goal, so without one there is nothing
  // for a new goal slot to say — and one already in the stack outlives the goal
  // it was written against, with nothing left to report until a goal returns.
  includeTypeInSlotInsertionOptions: context => context.hasImpressionGoal,
  isCurrentlyUsable: context => context.hasImpressionGoal,
};

const OPTIONAL: SlotTypeLogic<OptionalSlot> = {
  ...exprSlotLogic,
  type: 'optional',
  name: 'Optional text',
  blank: () => ({
    type: 'optional',
    id: newSlotId(),
    expr: ' optional text',
  }),
  warning: exprWarning(text => text.trim() === ''),
  isAnswerable: true,
  possibleValues: (slot, addressingMode) =>
    addressingMode === undefined ||
    offeredInAddressingMode(slot, addressingMode)
      ? optionalValues(slot)
      : [],
  // The offered text keeps its own spacing rather than being trimmed: the
  // leading space in ' {_recipientInformalName}' is what separates it from the
  // word before it, and leaving the slot out has to space the sentence the
  // same way.
  answerExpr: optionalAnswer,
  asStatements: slot => [{ kind: 'emit', expr: optionalAnswer(slot) }],
};

/** What an optional prints: its text when put in, `''` when left out. */
function optionalAnswer(slot: AnsweredSlot<OptionalSlot>): Expression {
  return slot.answer === slot.expr
    ? ExpressionLogic.template(slot.expr, 'tidied')
    : ExpressionLogic.quote(slot.answer);
}

/**
 * The two answers an optional slot declares: leave the text out, or put it in.
 * Both are declared in either addressing mode — what a mode decides is which
 * of them the sender is OFFERED, not what an answer already stored means.
 */
function optionalValues(slot: OptionalSlot): string[] {
  return ['', slot.expr];
}

/** Whether an optional slot's text is offered in this addressing mode. */
function offeredInAddressingMode(
  slot: OptionalSlot,
  addressingMode: AddressingMode
): boolean {
  const gate = slot.forAddressingModes ?? 'all';
  return gate === 'all' || gate === addressingMode;
}

const SELECT: SlotTypeLogic<SelectSlot> = {
  ...fieldSlotLogic,
  type: 'select',
  name: 'Multiple choice',
  takesVariable: true,
  // A new select starts with one option, so it is valid the moment it lands;
  // the author fills the rest in from the expanded editor.
  blank: () => ({
    type: 'select',
    id: newSlotId(),
    prompt: 'Choice',
    options: ['Option 1'],
  }),
  warning: slot => {
    const missingPrompt = promptWarning(slot);
    if (missingPrompt) return missingPrompt;
    const presets = presetOptions(slot);
    if (presets.length === 0) return 'Needs at least one option';
    if (presets.some(opt => opt.trim() === '')) return 'Has an empty option';
    if (new Set(presets).size !== presets.length)
      return 'Has duplicate options';
    return null;
  },
  acceptsFreeText: slot => slot.writeIn !== undefined,
  possibleValues: presetOptions,
  answerExpr: selectAnswer,
  asStatements: slot => fieldStatements(slot, selectAnswer(slot)),
};

/**
 * What a select prints: a picked option as a template, keeping its own
 * spacing as an optional's text does — a leading space in ' {_profileName}'
 * is what separates it from the word before it. Anything else is what the
 * sender typed into the write-in, which reaches the message as they wrote it.
 */
function selectAnswer(slot: AnsweredSlot<SelectSlot>): Expression {
  const picked = presetOptions(slot).find(value => value === slot.answer);
  return picked === undefined
    ? ExpressionLogic.quote(slot.answer)
    : ExpressionLogic.template(picked, 'tidied');
}

const FILL_IN: SlotTypeLogic<FillInSlot> = {
  ...fieldSlotLogic,
  type: 'fillIn',
  name: 'Fill in',
  takesVariable: true,
  blank: () => ({
    type: 'fillIn',
    id: newSlotId(),
    prompt: 'Answer',
    maxlength: FREE_TEXT_MAX_LENGTH_DEFAULT,
  }),
  warning: promptWarning,
  // Free text throughout: it declares nothing to pick from, so whatever the
  // sender types is what the message carries.
  acceptsFreeText: () => true,
  answerExpr: slot => ExpressionLogic.quote(slot.answer),
  asStatements: slot =>
    fieldStatements(slot, ExpressionLogic.quote(slot.answer)),
};

/**
 * Every slot type's logic, by tag. `satisfies` is what makes this exhaustive:
 * a new member of the slot union with no entry here is a compile error naming
 * exactly the type that is missing.
 */
export const SLOT_LOGIC = {
  text: TEXT,
  goal: GOAL,
  optional: OPTIONAL,
  select: SELECT,
  fillIn: FILL_IN,
} as const satisfies { readonly [T in SlotType]: SlotTypeLogic<SlotOfType<T>> };

/**
 * The logic for a slot, or for a type named on its own (the Add-slot sheet
 * knows only the type it is offering).
 */
function logic(slotOrType: SharingFormSlot | SlotType): SlotTypeLogic {
  return SLOT_LOGIC[
    typeof slotOrType === 'string' ? slotOrType : slotOrType.type
  ];
}

// ── Reading a slot ──────────────────────────────────────────────────────────

function typeOf(slot: SharingFormSlot): SlotType {
  return slot.type;
}

/** The type's author-facing name — for an eyebrow, an aria label, a menu row. */
function typeName(type: SlotType): string {
  return logic(type).name;
}

/** The one-line summary a collapsed card shows. */
function summary(slot: SharingFormSlot): string {
  return logic(slot).summary(slot);
}

/**
 * The type name for assistive tech on a collapsed card. Collapsed cards are
 * icon-only by design — the badge, container treatment and preview content
 * already encode the type three times over — so the name is carried here rather
 * than as visible eyebrow text.
 */
function ariaLabel(slot: SharingFormSlot): string {
  return `${logic(slot).name} slot: ${summary(slot)}`;
}

/**
 * Why a slot is not yet valid, or null when it is fine. Validation gates
 * gently: an offending card still collapses and still carries its warning, but
 * the screen-level Done refuses while any warning stands.
 *
 * This is what the slot can say about itself. What depends on the rest of its
 * form — which variables are set before it — is `warningInStack`.
 */
function warning(
  slot: SharingFormSlot,
  topicType?: TopicTypeId
): string | null {
  return logic(slot).warning(slot, topicType) ?? variableNameWarning(slot);
}

/** Why the variable a slot assigns cannot be used as named, or null. */
function variableNameWarning(slot: SharingFormSlot): string | null {
  const variable = declaredVariableName(slot);
  if (variable === null) return null;
  if (variable === '') return 'Needs a variable name';
  if (isProcedureName(variable)) {
    return `"${variable}" names a procedure; pick another variable name`;
  }
  return isVariableName(variable)
    ? null
    : `Variable name "${variable}" must start with a letter and use only letters, digits and _`;
}

/**
 * Why the slot `id` names is not yet valid within its form: its own warning,
 * then anything wrong with the variables it assigns or reads (see
 * `variableIssues` in `./schema`, which the parser enforces too).
 */
function warningInStack(
  slots: readonly SharingFormSlot[],
  id: string,
  topicType?: TopicTypeId
): string | null {
  const index = slots.findIndex(slot => slot.id === id);
  const slot = slots[index];
  if (slot === undefined) return null;
  return (
    warning(slot, topicType) ??
    variableIssues(slots, topicType).find(issue => issue.index === index)
      ?.message ??
    null
  );
}

/**
 * Whether a slot still does its job against the topic on screen. An unusable
 * slot stays in the stack — still editable, still deletable — and how that
 * reads is the UI's call.
 */
function isUsable(slot: SharingFormSlot, context: FormBuilderContext): boolean {
  return logic(slot).isCurrentlyUsable(context);
}

/** Whether the ＋ Add slot sheet offers this type against the topic on screen. */
function offersTypeForInsertion(
  type: SlotType,
  context: FormBuilderContext
): boolean {
  return logic(type).includeTypeInSlotInsertionOptions(context);
}

/**
 * Whether a field slot's prompt is blank. Its card then stays open until the
 * author fills the prompt in.
 */
function lacksPrompt(slot: SharingFormSlot): boolean {
  return (
    (slot.type === 'select' || slot.type === 'fillIn') &&
    slot.prompt.trim() === ''
  );
}

/** Every slot warning in a template's stack, in stack order. */
function stackWarnings(
  slots: readonly SharingFormSlot[],
  topicType?: TopicTypeId
): string[] {
  return slots
    .map(slot => warningInStack(slots, slot.id, topicType))
    .filter((warning): warning is string => warning !== null);
}

// ── Select slots: options and the "Other" write-in ──────────────────────────

/** A select's preset options. */
function presetOptions(select: SelectSlot): string[] {
  return [...(select.options ?? [])];
}

/** A select's free-text write-in, or null when it offers none. */
function writeInOf(select: SelectSlot): NestedWriteIn | null {
  return select.writeIn ?? null;
}

function withPresets(
  select: SelectSlot,
  presets: readonly string[]
): SelectSlot {
  return { ...select, options: [...presets] };
}

function setPresetOption(
  select: SelectSlot,
  index: number,
  value: string
): SelectSlot {
  return withPresets(
    select,
    presetOptions(select).map((opt, i) => (i === index ? value : opt))
  );
}

function addPresetOption(select: SelectSlot, value = ''): SelectSlot {
  return withPresets(select, [...presetOptions(select), value]);
}

function removePresetOption(select: SelectSlot, index: number): SelectSlot {
  return withPresets(
    select,
    presetOptions(select).filter((_, i) => i !== index)
  );
}

function movePresetOption(
  select: SelectSlot,
  from: number,
  to: number
): SelectSlot {
  return withPresets(select, moveItem(presetOptions(select), from, to));
}

/**
 * Turn the "Other" write-in on or off. Turning it on seeds the label and the
 * shorter write-in default limit; turning it off drops the write-in, which is
 * what removes the free-text alternative from the recipient's options.
 */
function setWriteInEnabled(select: SelectSlot, enabled: boolean): SelectSlot {
  if (enabled === (select.writeIn !== undefined)) return select;
  if (!enabled) {
    const next = { ...select };
    delete next.writeIn;
    return next;
  }
  return {
    ...select,
    writeIn: { label: 'Other', maxlength: OTHER_WRITE_IN_MAX_LENGTH_DEFAULT },
  };
}

function setWriteInMaxLength(
  select: SelectSlot,
  maxlength: number
): SelectSlot {
  if (!select.writeIn) return select;
  return { ...select, writeIn: { ...select.writeIn, maxlength } };
}

/** The hint shown in the empty write-in field; `''` removes it. */
function setWriteInPlaceholder(
  select: SelectSlot,
  placeholder: string
): SelectSlot {
  if (!select.writeIn) return select;
  const writeIn: NestedWriteIn = { ...select.writeIn, placeholder };
  if (placeholder === '') delete writeIn.placeholder;
  return { ...select, writeIn };
}

/**
 * Which addressing modes an optional slot is offered in. `dm` keeps it to
 * messages that reach exactly one person — what text naming that person needs,
 * since `{_recipientInformalName}` resolves against the FIRST recipient and a
 * group text would carry that one name to the whole batch; `group-text` is its
 * mirror. `all` is the absence of the key, so a form that never cared about
 * this dumps exactly as it always did.
 */
function setAddressingModes(
  slot: OptionalSlot,
  gate: AddressingModeGate
): OptionalSlot {
  if (gate !== 'all') return { ...slot, forAddressingModes: gate };
  const next = { ...slot };
  delete next.forAddressingModes;
  return next;
}

/**
 * Which sharing modes a slot is offered in — the pitch that belongs to a fresh
 * share, the story that belongs to a reshare, or text that belongs to both.
 * Every slot type takes this one (unlike the addressing gate, which only an
 * optional slot has), and `all` is again the absence of the key.
 */
function setSharingModes<S extends SharingFormSlot>(
  slot: S,
  gate: SharingModeGate
): S {
  if (gate !== 'all') return { ...slot, forSharingModes: gate };
  const next = { ...slot };
  delete next.forSharingModes;
  return next;
}

// ── Variables: define and dontEmit ──────────────────────────────────────────

/** The slot types that take `define` and `dontEmit`. */
export type VariableSlot = TextSlot | SelectSlot | FillInSlot;

/** Whether this slot can assign a variable and be left out of the message. */
function takesVariable(slot: SharingFormSlot): slot is VariableSlot {
  return logic(slot).takesVariable;
}

/**
 * The variable this slot assigns, or null when it assigns none. `''` is a
 * variable the author has asked for but not yet named.
 */
function declaredVariableName(slot: SharingFormSlot): string | null {
  return takesVariable(slot) ? (slot.define ?? null) : null;
}

/**
 * The statements of a select or fill-in whose answer prints as `value`:
 * define the variable as `value` unless the answer is blank, so the variable
 * keeps whatever an earlier slot gave it, then emit the variable, or `value`
 * itself when the slot defines none, unless `dontEmit`.
 */
function fieldStatements(
  slot: AnsweredSlot<SelectSlot | FillInSlot>,
  value: Expression
): readonly Statement[] {
  const defines = slot.define && slot.answer.trim() !== '';
  const statements: Statement[] = defines
    ? [{ kind: 'define', name: slot.define as string, expr: value }]
    : [];
  if (slot.dontEmit !== true)
    statements.push({
      kind: 'emit',
      expr: defines ? ExpressionLogic.variable(slot.define as string) : value,
    });
  return statements;
}

/**
 * Assign the slot's printed answer to a variable, or stop assigning it (null).
 * No variable is the absence of the key, so a form that never used one dumps
 * as it always did.
 */
function setDefine<S extends VariableSlot>(slot: S, name: string | null): S {
  if (name !== null) return { ...slot, define: name };
  const next = { ...slot };
  delete next.define;
  return next;
}

/**
 * Whether the slot emits into the message body. A slot that does not is still
 * asked, still required, and still assigns its variable; only the body leaves
 * it out.
 */
function emitsInMessage(slot: SharingFormSlot): boolean {
  return !takesVariable(slot) || slot.dontEmit !== true;
}

/**
 * Include the slot in the message body, or leave it out. Emitting is the
 * absence of `dontEmit`, so turning it back on removes the key.
 */
function setEmitsInMessage<S extends VariableSlot>(slot: S, emits: boolean): S {
  if (!emits) return { ...slot, dontEmit: true };
  const next = { ...slot };
  delete next.dontEmit;
  return next;
}

// ── Composing a message ─────────────────────────────────────────────────────

/** Whether the sender answers this slot while composing. */
function isAnswerable(slot: SharingFormSlot): boolean {
  return logic(slot).isAnswerable;
}

/** Whether the sender may answer this slot in their own words. */
function acceptsFreeText(slot: SharingFormSlot): boolean {
  return logic(slot).acceptsFreeText(slot);
}

/**
 * The answers this slot can take, in the form an answer is stored in: every
 * answer it declares, or — given an addressing mode — just the ones the sender
 * is offered in that mode.
 */
function possibleValues(
  slot: SharingFormSlot,
  addressingMode?: AddressingMode
): readonly string[] {
  return logic(slot).possibleValues(slot, addressingMode);
}

/**
 * Whether any answer this slot offers names whoever sends the message — an
 * option reading `{_profileName}` — so the names on the sender's profile are
 * what it puts in front of them.
 */
function namesSender(slot: SharingFormSlot): boolean {
  return possibleValues(slot).some(value =>
    ExpressionLogic.freeNames(ExpressionLogic.template(value, 'tidied')).some(
      name => PRIMITIVES[name as PrimitiveName]?.perSender === true
    )
  );
}

/** The slot with this answer on it, ready to be composed with. */
function withAnswer<S extends SharingFormSlot>(
  slot: S,
  answer: string
): AnsweredSlot<S> {
  return { ...slot, answer };
}

/** The same slot under another id, which it also answers under. */
function withId<S extends SharingFormSlot>(slot: S, id: string): S {
  return { ...slot, id };
}

/**
 * What a slot the sender answers (a select, fill-in or optional) prints in
 * `env`, the environment at its position (`EvaluatedForm.slotEnvironments`),
 * given the answer it carries: its answer expression (`answerExpr`),
 * evaluated there. A control labels each answer it offers with
 * `evaluate(withAnswer(slot, value), env)`. A text or goal slot evaluates to
 * `''`.
 */
function evaluate(slot: AnsweredSlot, env: Environment): string {
  const expr = logic(slot).answerExpr(slot);
  return expr === null ? '' : ExpressionLogic.print(expr, env);
}

/** The statements this slot stands for (see `Statement`). */
function asStatements(slot: AnsweredSlot): readonly Statement[] {
  return logic(slot).asStatements(slot);
}

/**
 * Whether a slot has nothing worth asking about in this mode: the sender
 * answers it, cannot answer it in their own words, and nothing it offers here
 * would reach the message. A `dm`-only optional in a group text offers nothing
 * at all; a select whose every option is a primitive that resolves empty
 * offers nothing that would land. Such a slot is left out of the form rather
 * than shown as a question with no usable answer.
 */
function isVestigial(
  slot: SharingFormSlot,
  env: Environment,
  addressingMode: AddressingMode
): boolean {
  const entry = logic(slot);
  if (!entry.isAnswerable || entry.acceptsFreeText(slot)) return false;
  if (entry.possibleValues(slot).length === 0) return false;
  return entry
    .possibleValues(slot, addressingMode)
    .every(value => evaluate(withAnswer(slot, value), env).trim() === '');
}

/**
 * Whether the sender has answered this slot, or has nothing to answer. A
 * vestigial slot is never waiting on them: it is not on screen to answer.
 */
function isAnswered(
  slot: AnsweredSlot,
  env: Environment,
  addressingMode: AddressingMode
): boolean {
  if (isVestigial(slot, env, addressingMode)) return true;
  return logic(slot).isAnswered(slot, env);
}

/**
 * Whether a slot is part of the message a sharing mode sends. `'all'` — the
 * editor's reading, which is looking at the form rather than at one message it
 * can produce — keeps every slot; a concrete mode keeps the slots that named
 * it and the ones that named no mode at all.
 */
function isCompatibleWithSharingMode(
  slot: SharingFormSlot,
  sharingMode: SharingModeGate
): boolean {
  if (sharingMode === 'all') return true;
  const gate = slot.forSharingModes ?? 'all';
  return gate === 'all' || gate === sharingMode;
}

// ── Slot-stack mutations ────────────────────────────────────────────────────

function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

function moveSlot(
  slots: readonly SharingFormSlot[],
  from: number,
  to: number
): SharingFormSlot[] {
  return moveItem(slots, from, to);
}

/** The slot with this id, or undefined when the stack holds none. */
function slotWithId(
  slots: readonly SharingFormSlot[],
  id: string
): SharingFormSlot | undefined {
  return slots.find(slot => slot.id === id);
}

/** The stack with the slot `id` names swapped for `next`. */
function replaceSlot(
  slots: readonly SharingFormSlot[],
  id: string,
  next: SharingFormSlot
): SharingFormSlot[] {
  return slots.map(slot => (slot.id === id ? next : slot));
}

function removeSlot(
  slots: readonly SharingFormSlot[],
  id: string
): SharingFormSlot[] {
  return slots.filter(slot => slot.id !== id);
}

function insertSlot(
  slots: readonly SharingFormSlot[],
  index: number,
  slot: SharingFormSlot
): SharingFormSlot[] {
  const next = [...slots];
  next.splice(index, 0, slot);
  return next;
}

/**
 * Insert a copy of the slot `id` names after it. The copy gets a fresh
 * UUID80, so the two are told apart and store their answers apart. The copy
 * is linked to nothing: a form holds each soft link at most once, and a
 * second copy in the same form is a new question, not the same one asked
 * again.
 */
function duplicateSlot(
  slots: readonly SharingFormSlot[],
  id: string
): SharingFormSlot[] {
  const index = slots.findIndex(slot => slot.id === id);
  if (index === -1) return [...slots];
  return insertSlot(
    slots,
    index + 1,
    withoutLink(withId(slots[index], newSlotId()))
  );
}

/** The slot's soft link, or null when it is linked to nothing. */
function linkIdOf(slot: SharingFormSlot): string | null {
  return slot.linkId ?? null;
}

/**
 * Whether two JSON-shaped values hold the same data, however their keys are
 * ordered. A key set to undefined counts as absent.
 */
function sameData(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((item, i) => sameData(item, b[i]))
    );
  }
  if (typeof a !== 'object' || typeof b !== 'object' || !a || !b) return false;
  const entries = (o: object) =>
    Object.entries(o).filter(([, value]) => value !== undefined);
  const left = entries(a);
  const right = new Map(entries(b));
  return (
    left.length === right.size &&
    left.every(
      ([key, value]) => right.has(key) && sameData(value, right.get(key))
    )
  );
}

/**
 * Whether two slots ask exactly the same thing: the same type, prompt,
 * options, text and every other setting. Only the slot id and the soft link
 * may differ, since those say which slot it is, not what it asks.
 */
function sameContent(a: SharingFormSlot, b: SharingFormSlot): boolean {
  // sameData counts an undefined key as absent.
  const content = (slot: SharingFormSlot) => ({
    ...slot,
    id: undefined,
    linkId: undefined,
  });
  return sameData(content(a), content(b));
}

/** The same slot with no soft link (see `SharingConfigLogic`). */
function withoutLink<S extends SharingFormSlot>(slot: S): S {
  if (slot.linkId === undefined) return slot;
  const next = { ...slot };
  delete next.linkId;
  return next;
}

// ── New slots (the ＋ Add slot sheet) ────────────────────────────────────────

/**
 * A blank slot of the given type, under a fresh UUID80 id. New field slots
 * start with a prompt but an empty option/placeholder set, so the author
 * fills them in the expanded editor.
 */
function blankSlot(type: SlotType): SharingFormSlot {
  return logic(type).blank();
}

/** Pure slot logic: the builder's slot stack, and what a slot composes. */
export const SlotLogic = {
  typeOf,
  typeName,
  presetOptions,
  writeInOf,
  setPresetOption,
  addPresetOption,
  removePresetOption,
  movePresetOption,
  setWriteInEnabled,
  setWriteInMaxLength,
  setWriteInPlaceholder,
  setAddressingModes,
  setSharingModes,
  takesVariable,
  declaredVariableName,
  setDefine,
  emitsInMessage,
  setEmitsInMessage,
  moveSlot,
  slotWithId,
  replaceSlot,
  removeSlot,
  insertSlot,
  duplicateSlot,
  linkIdOf,
  sameContent,
  withoutLink,
  blankSlot,
  newSlotId,
  summary,
  ariaLabel,
  warning,
  lacksPrompt,
  warningInStack,
  stackWarnings,
  isUsable,
  offersTypeForInsertion,
  isAnswerable,
  withAnswer,
  withId,
  acceptsFreeText,
  possibleValues,
  namesSender,
  evaluate,
  asStatements,
  isVestigial,
  isAnswered,
  isCompatibleWithSharingMode,
};
